import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { hasMinRole } from '../../utils/roles';
import EnvelopeDetailDialog from './EnvelopeDetailDialog';
import { ESIGN_STATUSES, apiError, esignStatusColor, formatDateTime } from './esignUtils';

/** Every in-house signing request, newest first (TKT-401). */
const SignaturesPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [provider, setProvider] = useState('');
  const [providerNotice, setProviderNotice] = useState('');
  const isSuperAdmin = hasMinRole(user, 'superadmin');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        status,
        limit: String(limit),
        offset: String(page * limit),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      const res = await makeApiCall(`api/esign/envelopes?${params}`);
      if (!res.ok) throw new Error(await apiError(res, res.statusText));
      const data = await res.json();
      setRows(data.envelopes || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(t('esign.detail.actionFailed', { error: e.message }));
    } finally {
      setLoading(false);
    }
  }, [status, limit, page, debouncedSearch, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await makeApiCall('api/esign/settings');
        if (res.ok) setProvider((await res.json()).defaultProvider);
      } catch {
        // The switch just stays hidden.
      }
    })();
  }, []);

  const saveProvider = async (value) => {
    setProviderNotice('');
    const res = await makeApiCall('api/esign/settings', {
      method: 'PUT',
      body: JSON.stringify({ defaultProvider: value }),
    });
    if (res.ok) {
      setProvider(value);
      setProviderNotice(t('esign.list.providerSaved'));
    } else {
      setError(t('esign.detail.actionFailed', { error: await apiError(res, res.statusText) }));
    }
  };

  const lang = i18n.language;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1c3366', mb: 0.5 }}>{t('esign.list.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('esign.list.subtitle')}</Typography>

      {isSuperAdmin && provider && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="esign-provider-label">{t('esign.list.defaultProvider')}</InputLabel>
              <Select
                labelId="esign-provider-label"
                label={t('esign.list.defaultProvider')}
                value={provider}
                onChange={(e) => saveProvider(e.target.value)}
              >
                <MenuItem value="docusign">{t('esign.list.providerDocusign')}</MenuItem>
                <MenuItem value="inhouse">{t('esign.list.providerInhouse')}</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">{providerNotice || t('esign.list.providerHelp')}</Typography>
          </Stack>
        </Paper>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label={t('esign.list.search')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ flex: 1, maxWidth: 420 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="esign-status-label">{t('esign.list.status')}</InputLabel>
          <Select
            labelId="esign-status-label"
            label={t('esign.list.status')}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">{t('esign.status.all')}</MenuItem>
            <MenuItem value="active">{t('esign.status.active')}</MenuItem>
            {ESIGN_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{t(`esign.status.${s}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('esign.list.document')}</TableCell>
                <TableCell>{t('esign.list.signer')}</TableCell>
                <TableCell>{t('esign.list.status')}</TableCell>
                <TableCell>{t('esign.list.sent')}</TableCell>
                <TableCell>{t('esign.list.lastChange')}</TableCell>
                <TableCell>{t('esign.list.sentBy')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{t('esign.list.empty')}</Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedId(row.id)}>
                  <TableCell>
                    <Typography variant="body2">{row.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.recipient_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.recipient_email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={esignStatusColor(row.status)} label={t(`esign.status.${row.status}`)} />
                  </TableCell>
                  <TableCell>{formatDateTime(row.sent_at, lang)}</TableCell>
                  <TableCell>{formatDateTime(row.completed_at || row.declined_at || row.voided_at, lang)}</TableCell>
                  <TableCell>{row.sender_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          rowsPerPageOptions={[25, 50, 100]}
          onRowsPerPageChange={(e) => {
            setLimit(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <EnvelopeDetailDialog
        envelopeId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </Box>
  );
};

export default SignaturesPage;
